import { Color, ColorLike } from "../math/color";
import { Texture, textureDefaultValueRange } from "../objects/textures/texture";
import { Logger } from "../utilities/logger";
import { clamp } from "../utilities/clamp";

const MAX_CHANNELS = 32;

export type Channel = {
  visible: boolean;
  color: Color;
  contrastLimits: [number, number];
  opacity: number;
};

/**
 * Appearance settings for a single image channel.
 */
export type ChannelProps = {
  /** Whether the channel is drawn. Defaults to `true`. */
  visible?: boolean;
  /** The channel's tint color. Defaults to white. */
  color?: ColorLike;
  /** Intensity range shown. Defaults to the data range. */
  contrastLimits?: [number, number];
  /** Channel opacity in `[0, 1]`. Defaults to `1`. */
  opacity?: number;
};

/** Layer that exposes channel controls. */
export interface ChannelsEnabled {
  channelProps: ChannelProps[] | undefined;
  setChannelProps(channelProps: ChannelProps[]): void;
  resetChannelProps(): void;
  addChannelChangeCallback(callback: () => void): void;
  removeChannelChangeCallback(callback: () => void): void;
}

export function validateChannel(
  texture: Texture | null,
  { visible, color, contrastLimits, opacity }: ChannelProps
): Channel {
  visible ??= true;
  color = color === undefined ? Color.WHITE : Color.from(color);
  opacity = opacity === undefined ? 1 : clamp(opacity, 0, 1);

  if (texture !== null) {
    contrastLimits = validateContrastLimits(contrastLimits, texture);
  } else if (contrastLimits === undefined) {
    Logger.debug(
      "Channel",
      "No texture provided, defaulting channel contrast limits to [0, 1]."
    );
    contrastLimits = [0, 1];
  }

  return {
    visible,
    color,
    contrastLimits,
    opacity,
  };
}

export function validateChannels(
  texture: Texture | null,
  channelProps: ChannelProps[]
): Channel[] {
  if (channelProps.length > MAX_CHANNELS) {
    throw new Error(`Maximum number of channels is ${MAX_CHANNELS}`);
  }

  return channelProps.map((props) => validateChannel(texture, props));
}

export function validateChannelPropsCount(
  channelProps: ChannelProps[] | undefined,
  sourceChannelCount: number
): void {
  if (channelProps && channelProps.length !== sourceChannelCount) {
    throw new Error(
      `channelProps length (${channelProps.length}) must match ` +
        `source channel count (${sourceChannelCount}).`
    );
  }
}

function validateContrastLimits(
  contrastLimits: [number, number] | undefined,
  texture: Texture
): [number, number] {
  if (contrastLimits === undefined) {
    return textureDefaultValueRange(texture);
  }
  if (contrastLimits[1] <= contrastLimits[0]) {
    throw new Error(
      `Contrast limits must be strictly increasing: ${contrastLimits}.`
    );
  }
  return contrastLimits;
}
